import { z } from "zod";
import { calendarDateFiltersSchema, filtersSchema } from "../filters/general";

export enum ViewDensity {
    SM = "sm",
    MD = "md",
    LG = "lg",
}

/** The most card tags a board view may save; boards offer far fewer fields. */
export const MAX_BOARD_CARD_FIELDS = 32;

export const ColumnOptionsSchema = z.object({
    pin: z.enum(["left", "right"]).optional(),
    minSize: z.number().optional(),
    maxSize: z.number().optional(),
    size: z.number().optional(),
    customVariants: z.record(z.string(), z.string()).optional(),
});

export const ViewOptionsSchema = z.object({
    bulkActions: z.boolean().optional(),
    selection: z.boolean().optional(),
    /**
     * Keeps row checkboxes visible instead of revealing them on row hover.
     * For tables where selecting is a primary action rather than a shortcut.
     */
    alwaysShowSelection: z.boolean().optional(),
    /**
     * Drops the select-all checkbox from the header. For tables whose rows
     * load lazily, where "all" could only ever mean "the rows fetched so far".
     */
    hideSelectAll: z.boolean().optional(),
    readonly: z.boolean().optional(),
    density: z.nativeEnum(ViewDensity).optional(),
    groupBy: z.string().nullable().optional(),
    /**
     * Board display settings the view keeps. Unset keys mean the page's
     * defaults; the page narrows `laneBy` to the lanes it offers.
     */
    board: z
        .object({
            /** What the swimlanes group by; null or unset means no swimlanes. */
            laneBy: z.string().max(64).nullable().optional(),
            isEmptyColumnsShown: z.boolean().optional(),
            /** Whether cards show the chat chip; unset means shown. */
            isCommentsShown: z.boolean().optional(),
            /** The card's tags, in order, instead of the visible columns; unset uses the columns. */
            cardFields: z
                .array(z.string().max(64))
                .max(MAX_BOARD_CARD_FIELDS)
                .optional(),
        })
        .optional(),
});

export const ColumnSchema = z.object({
    id: z.string(),
    options: ColumnOptionsSchema.nullable().optional(),
});

export enum ViewType {
    TRANSACTION = "transaction",
    WORKSPACE = "workspace",
    ACCOUNTS = "accounts",
    CLIENT = "client",
    TEAM = "team",
    INVOICE = "invoice",
    INVOICES_BY_CUSTOMER = "invoicesByCustomer",
    INVOICE_LINE_ITEM = "invoiceLineItem",
    CATALOG_ITEM = "catalogItem",
    /** Ephemeral only — not persisted to DB. Used for invoice templates list view column visibility. */
    INVOICE_TEMPLATE = "invoiceTemplate",
    BILL = "bill",
    BILLS_BY_VENDOR = "billsByVendor",
    BILL_LINE_ITEM = "billLineItem",
    TASK = "task",
    TASK_GROUP = "taskGroup",
    FINANCIAL_DOCUMENT = "financialDocument",
    BANK_RECONCILIATION = "bankReconciliation",
    RECEIPT_SENDER = "receiptSender",
    EMAIL_ALIAS = "emailAlias",
    CHART_OF_ACCOUNTS = "chartOfAccounts",
    HISTORY = "history",
    HISTORY_TRANSACTION = "historyTransaction",
    HISTORY_FINANCIAL_ACCOUNT = "historyFinancialAccount",
    HISTORY_CREATED_TRANSACTION = "historyCreatedTransaction",
    HISTORY_UPDATED_TRANSACTION = "historyUpdatedTransaction",
    HISTORY_DELETED_TRANSACTION = "historyDeletedTransaction",
    HISTORY_SPLIT_PARENT_TRANSACTION = "historySplitParentTransaction",
    HISTORY_SPLIT_VERSIONS = "historySplitVersions",
    HISTORY_SPLIT_DETAILS = "historySplitDetails",
    HISTORY_BATCH_GROUPS = "historyBatchGroups",
    HISTORY_TASK_UPDATE = "historyTaskUpdate",
    /** Ephemeral only — not persisted to DB. Used for field review audit log detail view. */
    HISTORY_FIELD_REVIEW = "historyFieldReview",

    HISTORY_RULE_LIST = "historyRuleList",
    HISTORY_TRANSFER_RULE_LIST = "historyTransferRuleList",
    HISTORY_RULE_DETAILS = "historyRuleDetails",
    HISTORY_MANUAL_JOURNAL_ENTRY = "historyManualJournalEntry",
    HISTORY_MANUAL_JOURNAL_ENTRY_VERSIONS = "historyManualJournalEntryVersions",
    HISTORY_MANUAL_JOURNAL_ENTRY_DETAILS = "historyManualJournalEntryDetails",
    /** Persisted to DB for task views and for saved P&L report views (`taskId` null). Also used ephemerally for the report table. */
    PROFIT_LOSS = "profitLoss",
    /** Persisted to DB for saved General Ledger report views. Also used ephemerally for the report table. */
    GENERAL_LEDGER = "generalLedger",
    /** Ephemeral only — not persisted to DB. The `view_type_enum` migration (`1773446686137-AddDocumentCategoriesView.ts`) exists but no `view` rows of this type should be created. */
    DOCUMENT_CATEGORIES = "documentCategories",
    /** Ephemeral only — not persisted to DB. The `view_type_enum` migration (`1773580000000-AddTransactionCategoriesView.ts`) exists but no `view` rows of this type should be created. */
    TRANSACTION_CATEGORIES = "transactionCategories",
    /** Ephemeral only — not persisted to DB. The `view_type_enum` migration (`1773580000000-AddTransactionCategoriesView.ts`) exists but no `view` rows of this type should be created. */
    CLASSES = "classes",
    /** Persisted to DB for task views and for saved Balance Sheet report views (`taskId` null). Also used ephemerally for the report table. */
    BALANCE_SHEET = "balanceSheet",
    /** Persisted to DB for saved Expenses by Vendor report views. Also used ephemerally for the report table. */
    EXPENSE_BY_VENDOR = "expenseByVendor",
    /** Ephemeral only — not persisted to DB. Used for close template list view column visibility. */
    CLOSE_TEMPLATES = "closeTemplates",
    /** Ephemeral only — not persisted to DB. Used for org-level task template list view column visibility. */
    ORGANIZATION_TASK_TEMPLATES = "organizationTaskTemplates",
    /** Ephemeral only — not persisted to DB. Used for chart of accounts template list view column visibility. */
    CHART_OF_ACCOUNTS_TEMPLATES = "chartOfAccountsTemplates",
    /** Ephemeral only — not persisted to DB. Used for report template list view column visibility. */
    REPORT_TEMPLATES = "reportTemplates",
    /** Ephemeral only — not persisted to DB. Used for the firm agent skills list view column visibility. */
    AGENT_SKILLS = "agentSkills",
    /** Ephemeral only — not persisted to DB. Used for the apply-CoA-template select-entities table column visibility/sizing. */
    CHART_OF_ACCOUNTS_TEMPLATE_APPLY = "chartOfAccountsTemplateApply",
    /** Ephemeral only — not persisted to DB. Used for the apply-report-template drawer table column visibility/sizing. */
    REPORT_TEMPLATE_APPLY = "reportTemplateApply",
    /** Persisted to DB for saved Cash Flow Statement report views. Also used ephemerally for the report table. */
    CASH_FLOW_STATEMENT = "cashFlowStatement",
    /** Persisted to DB (varchar column) for saved Schedules report views. Also used ephemerally for the report table. */
    SCHEDULES_REPORT = "schedulesReport",
    /** Persisted to DB. Used for org clients-close-overview page custom views. */
    CLIENTS_CLOSE_OVERVIEW = "clientsCloseOverview",
    /** Persisted to DB. Used for org clients-work-overview page custom views. */
    CLIENTS_WORK_OVERVIEW = "clientsWorkOverview",
    /** Persisted for form 1099 task views. Also used ephemerally for counterparty directory column visibility and sizing. */
    COUNTERPARTY_DIRECTORY = "counterpartyDirectory",
    /** Persisted to DB for saved Trial Balance report views. Also used ephemerally for the report table. */
    TRIAL_BALANCE = "trialBalance",
    /** Persisted to DB for reconciliation task views. Stores financialAccountTypes in filters. */
    RECONCILIATION = "reconciliation",
    /** Ephemeral only — not persisted to DB. Used for workspace members settings table. */
    WORKSPACE_MEMBERS = "workspaceMembers",
    /** Ephemeral only — not persisted to DB. Used for the org-view integrations table column visibility/sizing. */
    ORGANIZATION_INTEGRATIONS = "organizationIntegrations",
    /** Ephemeral only — not persisted to DB. Used for accounting migration settings table. */
    ACCOUNTING_MIGRATIONS = "accountingMigrations",
    /** Ephemeral only — not persisted to DB. Used for accrual ledger settings table. */
    ACCRUAL_LEDGER_SETTINGS = "accrualLedgerSettings",
    /** Ephemeral only — not persisted to DB. Used for service template schedules list view column visibility. */
    SERVICE_TEMPLATE_SCHEDULES = "serviceTemplateSchedules",
    /** Ephemeral only — not persisted to DB. Used for the upcoming-occurrences preview tables in the schedule wizard and drawer. */
    SERVICE_TEMPLATE_SCHEDULE_OCCURRENCES = "serviceTemplateScheduleOccurrences",
    /** Ephemeral only — not persisted to DB. Used for the design-token playground catalog tables. */
    DESIGN_TOKENS = "designTokens",
    /** Ephemeral only — not persisted to DB. Used for org billing clients-on-invoice breakdown. */
    ORGANIZATION_BILLING_CLIENTS = "organizationBillingClients",
    /** Ephemeral only — not persisted to DB. Used for org billing invoice history. */
    ORGANIZATION_BILLING_INVOICES = "organizationBillingInvoices",
    /** Ephemeral only — not persisted to DB. Used for the Schedules hub list. */
    ACCOUNTING_SCHEDULES = "accountingSchedules",
    /** Ephemeral only — not persisted to DB. Used for schedule recognition previews. */
    SCHEDULE_PREVIEW = "schedulePreview",
    /** Ephemeral only — not persisted to DB. Used for the Schedule applications drill-down. */
    SCHEDULE_APPLICATIONS = "scheduleApplications",
    /** Ephemeral only — not persisted to DB. Used for the revenue recognition review task table. */
    SCHEDULE_RECOGNITION_REVIEW = "scheduleRecognitionReview",
    /** Ephemeral only — not persisted to DB. Used for the milestone review task's confirm step. */
    MILESTONE_REVIEW = "milestoneReview",
    /** Ephemeral only — not persisted to DB. Used for the counterparty editor contacts table. */
    COUNTERPARTY_CONTACTS = "counterpartyContacts",
    /** Ephemeral only — not persisted to DB. Used for the counterparty editor addresses table. */
    COUNTERPARTY_ADDRESSES = "counterpartyAddresses",
    /** Ephemeral only — not persisted to DB. Used for the org-view member access log table column visibility/sizing. */
    ORGANIZATION_MEMBER_ACCESS_LOG = "organizationMemberAccessLog",
    /** Ephemeral only — not persisted to DB. Used for the org-view activity table column visibility/sizing. */
    ORGANIZATION_ACTIVITY = "organizationActivity",
    /** Ephemeral only — not persisted to DB. Used for the agent usage table column visibility/sizing. */
    AGENT_USAGE = "agentUsage",
    /** Ephemeral only — not persisted to DB. Used for the A/R Aging Summary report table. */
    AR_AGING_SUMMARY = "arAgingSummary",
    /** Ephemeral only — not persisted to DB. Used for the A/R Aging Detail report table. */
    AR_AGING_DETAIL = "arAgingDetail",
    /** Ephemeral only — not persisted to DB. Used for the Check Detail Report table. */
    CHECK_DETAIL = "checkDetail",
    /** Ephemeral only - not persisted to DB. Used for the 1099-NEC report table. */
    FORM_1099_NEC = "form1099Nec",
    /** Drafts importer table. Saved views persist on the varchar `view.type` column. */
    ACCRUAL_IMPORTER = "accrualImporter",
}

export const ViewSchema = z.object({
    type: z.nativeEnum(ViewType).optional(),
    columns: z.array(ColumnSchema),
    filters: filtersSchema.merge(calendarDateFiltersSchema).strip(),
    options: ViewOptionsSchema.nullable().optional(),
    originalFilters: filtersSchema
        .merge(calendarDateFiltersSchema)
        .strip()
        .nullable()
        .optional(),
});

export const PersistableViewSchema = ViewSchema.extend({
    type: z.nativeEnum(ViewType),
});

export const PersistedViewSchema = PersistableViewSchema.extend({
    id: z.string().uuid(),
    name: z.string(),
    taskId: z.string().nullable().optional(),
});

export type NamedView<T extends View> = T & {
    id: string;
    name: string;
};

export type PersistableView = z.infer<typeof PersistableViewSchema>;
export type PersistedView = z.infer<typeof PersistedViewSchema>;
export type ViewOptions = z.infer<typeof ViewOptionsSchema>;
export type View = z.infer<typeof ViewSchema>;
export type ColumnOptions = z.infer<typeof ColumnOptionsSchema>;
export type ColumnPin = NonNullable<ColumnOptions["pin"]>;
export type Column = z.infer<typeof ColumnSchema>;

export interface WorkspaceViewScope {
    workspaceId: string;
    organizationId?: never;
}

export interface OrganizationViewScope {
    organizationId: string;
    workspaceId?: never;
}

export const ViewScopeSchema = z.union([
    z.object({
        workspaceId: z.string().uuid(),
        organizationId: z.never().optional(),
    }),
    z.object({
        organizationId: z.string().uuid(),
        workspaceId: z.never().optional(),
    }),
]);

export type ViewScope = z.infer<typeof ViewScopeSchema>;
export function isWorkspaceViewScope(
    scope: ViewScope,
): scope is WorkspaceViewScope {
    return "workspaceId" in scope;
}

interface GetViewScopeParams {
    workspaceId?: string;
    organizationId?: string;
}

export function getViewScope(params: GetViewScopeParams): ViewScope {
    const { workspaceId, organizationId } = params;
    if (workspaceId) {
        return { workspaceId };
    }
    if (organizationId) {
        return { organizationId };
    }
    throw new Error("No workspace or organization selected");
}
